/**
 * Topics Index Loader
 * Håndterer loading af topic manifest og config
 * Prøver Firebase først, så JSON fallback
 */

import { loadIndexFromFirebase, loadTopicFromFirebase } from './firebaseLoader.js'

let indexData = null

/**
 * Load index.json
 * Prøver Firebase først, så JSON fallback
 */
export async function loadIndex() {
  if (indexData) {
    return indexData
  }
  
  // Try Firebase first
  try {
    const firebaseIndex = await loadIndexFromFirebase()
    indexData = firebaseIndex
    return firebaseIndex
  } catch (firebaseError) {
    console.warn('Firebase load failed, trying JSON fallback:', firebaseError.message)
  }
  
  // Fallback to JSON files
  try {
    // Try multiple paths (relative first, then absolute)
    // Determine base path based on current location
    const currentPath = window.location.pathname
    const isDist = currentPath.includes('/dist/') || currentPath.startsWith('/dist')
    const basePath = isDist ? './data/topics' : '/data/topics'
    
    const paths = [
      './data/topics/index.json', // Relative path (works from any location)
      `${basePath}/index.json`,    // Base path based on location
      '/data/topics/index.json',   // Production path (root)
      '/dist/data/topics/index.json', // If app is in /dist/
      'data/topics/index.json',    // No leading slash
      '/src/data/topics/index.json' // Dev path
    ]
    
    let response
    let lastError
    for (const path of paths) {
      try {
        response = await fetch(path)
        if (response.ok) {
          // Loaded index.json successfully
          console.log(`✅ Loaded index.json from: ${path}`)
          break
        }
      } catch (e) {
        lastError = e
        continue
      }
    }
    
    if (!response || !response.ok) {
      console.error('Failed to load index.json from all paths. Last error:', lastError)
      throw new Error(`Could not load index.json from any path. Tried: ${paths.join(', ')}`)
    }
    
    indexData = await response.json()
    return indexData
  } catch (error) {
    console.error('Error loading index.json:', error)
    // Fallback til hardcoded data hvis fil ikke findes
    return getDefaultIndex()
  }
}

/**
 * Get enabled topics
 */
export async function getEnabledTopics() {
  const index = await loadIndex()
  return index.topics.filter(topic => topic.enabled)
}

/**
 * Get topics by audience
 */
export async function getTopicsByAudience(audience) {
  const topics = await getEnabledTopics()
  return topics.filter(topic => 
    topic.audiences.includes(audience)
  )
}

/**
 * Get free topics
 */
export async function getFreeTopics() {
  const topics = await getEnabledTopics()
  return topics.filter(topic => topic.tier === 'free')
}

/**
 * Get paid topics
 */
export async function getPaidTopics() {
  const topics = await getEnabledTopics()
  return topics.filter(topic => topic.tier === 'paid')
}

/**
 * Get response type config
 */
export function getResponseTypeConfig(responseType) {
  // This will be loaded from index.json, but for now return hardcoded
  const responseTypes = {
    YNM: {
      name: "Ja/Nej/Måske",
      values: {
        "yes": 1.0,
        "maybe": 0.5,
        "no": 0.0
      },
      labels: {
        "yes": "Ja",
        "maybe": "Måske",
        "no": "Nej"
      }
    },
    IMPORTANCE_0_3: {
      name: "Vigtighed",
      values: {
        "0": 0.0,
        "1": 0.33,
        "2": 0.67,
        "3": 1.0
      },
      labels: {
        "0": "Ligegyldigt",
        "1": "Lidt vigtigt",
        "2": "Vigtigt",
        "3": "Meget vigtigt"
      }
    },
    FREQUENCY_0_4: {
      name: "Hyppighed",
      values: {
        "0": 0.0,
        "1": 0.25,
        "2": 0.5,
        "3": 0.75,
        "4": 1.0
      },
      labels: {
        "0": "Aldrig",
        "1": "Sjældent",
        "2": "Nogle gange",
        "3": "Ofte",
        "4": "Altid"
      }
    }
  }
  
  return responseTypes[responseType]
}

/**
 * Load a specific topic file
 * Prøver Firebase først, så JSON fallback
 */
export async function loadTopic(topicId) {
  try {
    const index = await loadIndex()
    const topic = index.topics.find(t => t.id === topicId)
    
    if (!topic) {
      throw new Error(`Topic ${topicId} not found`)
    }
    
    // Try Firebase first
    try {
      const firebaseTopic = await loadTopicFromFirebase(topicId)
      return firebaseTopic
    } catch (firebaseError) {
      console.warn(`Firebase load failed for ${topicId}, trying JSON fallback:`, firebaseError.message)
    }
    
    // Fallback to JSON files
    // Try multiple paths (relative first, then absolute)
    // Determine base path based on current location
    const currentPath = window.location.pathname
    const isDist = currentPath.includes('/dist/') || currentPath.startsWith('/dist')
    const basePath = isDist ? './data/topics' : '/data/topics'
    
    const paths = [
      `./data/topics/${topic.file}`, // Relative path (works from any location)
      `${basePath}/${topic.file}`,    // Base path based on location
      `/data/topics/${topic.file}`,   // Production path (root)
      `/dist/data/topics/${topic.file}`, // If app is in /dist/
      `data/topics/${topic.file}`,    // No leading slash
      `/src/data/topics/${topic.file}` // Dev path
    ]
    
    let response
    let lastError
    for (const path of paths) {
      try {
        response = await fetch(path)
        if (response.ok) {
          // Loaded topic file successfully
          console.log(`✅ Loaded ${topic.file} from: ${path}`)
          break
        }
      } catch (e) {
        lastError = e
        continue
      }
    }
    
    if (!response || !response.ok) {
      console.error(`Failed to load ${topic.file} from all paths. Last error:`, lastError)
      throw new Error(`Could not load topic file: ${topic.file}. Tried: ${paths.join(', ')}`)
    }
    
    const topicData = await response.json()
    
    return topicData
  } catch (error) {
    console.error(`Error loading topic ${topicId}:`, error)
    throw error
  }
}

/**
 * Default index (fallback)
 */
function getDefaultIndex() {
  return {
    schemaVersion: 2,
    topics: [],
    responseTypes: {}
  }
}
